import React, { useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
const DESKTOP_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  showNav: false,
  thumbnailPosition: 'left',
  showFullscreenButton: false,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;
const MAX_PORTRAIT_ASPECT_RATIO_DESKTOP = 3 / 4;

const getFirstImageAspectRatio = (firstImage, scaledVariant, isDesktop = false) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  const maxPortrait = isDesktop ? MAX_PORTRAIT_ASPECT_RATIO_DESKTOP : MAX_PORTRAIT_ASPECT_RATIO;
  const portraitBox = isDesktop
    ? { aspectWidth: 3, aspectHeight: 4 }
    : { aspectWidth: 4, aspectHeight: 3 };

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= maxPortrait
    ? portraitBox
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const intl = useIntl();
  const { rootClassName, className, images, imageVariants, thumbnailVariants } = props;
  const thumbVariants = thumbnailVariants || imageVariants;
  // imageVariants are scaled variants.
  const mobileRatio = getFirstImageAspectRatio(images?.[0], imageVariants[0], false);
  const desktopRatio = getFirstImageAspectRatio(images?.[0], imageVariants[0], true);
  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
    };
  });
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
  const makeRenderItem = ({ aspectWidth, aspectHeight }) => item => {
  return (
    <AspectRatioWrapper
      width={aspectWidth || 1}
      height={aspectHeight || 1}
      className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
    >
      <div className={css.itemCentering}>
        <ResponsiveImage
          rootClassName={css.item}
          image={item.image}
          alt={item.alt}
          variants={imageVariants}
          {...imageSizesMaybe}
        />
      </div>
    </AspectRatioWrapper>
  );
};

const renderItemMobile = makeRenderItem(mobileRatio);
const renderItemDesktop = makeRenderItem(desktopRatio);
  const renderThumbInner = item => {
    return (
      <div>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button
        className={css.navLeft}
        disabled={disabled}
        onClick={onClick}
        aria-label={intl.formatMessage({ id: 'ListingImageGallery.prevImageAriaLabel' })}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button
        className={css.navRight}
        disabled={disabled}
        onClick={onClick}
        aria-label={intl.formatMessage({ id: 'ListingImageGallery.nextImageAriaLabel' })}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: images.length }}
        />
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <>
      <div className={css.galleryDefault}>
        <ReactImageGallery
          additionalClass={classes}
          items={items}
          renderItem={renderItemMobile}
          renderThumbInner={renderThumbInner}
          onScreenChange={onScreenChange}
          renderLeftNav={renderLeftNav}
          renderRightNav={renderRightNav}
          renderFullscreenButton={renderFullscreenButton}
          {...IMAGE_GALLERY_OPTIONS}
        />
      </div>

      <div className={css.galleryDesktop}>
        <ReactImageGallery
          additionalClass={classes}
          items={items}
          renderItem={renderItemDesktop}
          renderThumbInner={renderThumbInner}
          onScreenChange={onScreenChange}
          renderLeftNav={renderLeftNav}
          renderRightNav={renderRightNav}
          renderFullscreenButton={renderFullscreenButton}
          {...DESKTOP_GALLERY_OPTIONS}
        />
      </div>
    </>
  );
};

export default ListingImageGallery;
